import React, { useEffect, useState } from 'react';

import { ArrowLeft, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';

import {
	useCreateRevenueConfigurationMutation,
	useGetRevenueConfigurationByIdQuery,
	useUpdateRevenueConfigurationMutation,
} from '../../api';
import { useToast } from '../../context/ToastContext';

import BasicInfoStep from './wizard/BasicInfoStep';
import RiskTierStep from './wizard/RiskTierStep';

import type { RevenueConfiguration } from './wizard/RiskTierStep';
import type { RevenueConfigTierInput, RiskTier } from '../../api';

interface RevenueConfigWizardProps {
	isOpen: boolean;
	onClose: () => void;
	onComplete: (configName: string) => void;
	editConfigId?: string;
	isFullPage?: boolean;
	onBack?: () => void;
}

const STEPS = [
	{ id: 1, name: 'Basic Info', title: 'Basic details' },
	{ id: 2, name: 'Risk Tier Rates', title: 'Schedule A Buy Rates' },
];

// Helper to create empty tier config
const createEmptyTierConfig = (tier: RiskTier): RevenueConfigTierInput => ({
	risk_tier: tier,
	// Legacy single rates
	// Storage format: percentage decimal (e.g., 0.0125 = 1.25 bps when converted)
	// User enters 1.25 bps → stored as 0.0125 → calculator divides by 100 → 0.000125
	discount_buy_rate: 0.0125, // Default 1.25 bps (stored as 0.0125, calculator uses 0.000125)
	authorization_buy_rate: 0.03, // Default $0.03
	// NEW: Split rates (optional, will fallback to legacy)
	discount_buy_rate_vmd: undefined,
	discount_buy_rate_amex: undefined,
	auth_buy_rate_standard: undefined,
	auth_buy_rate_pin_debit: undefined,
	auth_buy_rate_ebt: undefined,
	// NEW: Batch fee
	batch_fee_rate: 0,
	// NEW: Dynamic monthly fees
	monthly_fee_costs: [],
	// Legacy monthly fees
	monthly_fee_cost_1: 0,
	monthly_fee_cost_1_label: '',
	monthly_fee_cost_2: 0,
	monthly_fee_cost_2_label: '',
	monthly_fee_cost_3: 0,
	monthly_fee_cost_3_label: '',
	agent_revenue_share_percent: 50, // Default 50%
});

const RevenueConfigWizard: React.FC<RevenueConfigWizardProps> = ({
	isOpen,
	onClose,
	onComplete,
	editConfigId,
	isFullPage = false,
	onBack,
}) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [configuration, setConfiguration] = useState<RevenueConfiguration>({
		name: '',
		description: '',
		is_active: false,
		tiers: {
			LOW: createEmptyTierConfig('LOW'),
			MEDIUM: createEmptyTierConfig('MEDIUM'),
			HIGH: createEmptyTierConfig('HIGH'),
			OTHER: createEmptyTierConfig('OTHER'),
		},
	});

	const [createRevenueConfig, { isLoading: isCreating }] =
		useCreateRevenueConfigurationMutation();
	const [updateRevenueConfig, { isLoading: isUpdating }] =
		useUpdateRevenueConfigurationMutation();

	const { data: existingConfigResponse, isLoading: isLoadingConfig } =
		useGetRevenueConfigurationByIdQuery(editConfigId || '', {
			skip: !editConfigId,
		});

	const { showToast } = useToast();

	// Load existing configuration when editing
	useEffect(() => {
		if (editConfigId && existingConfigResponse?.data) {
			const configs = existingConfigResponse.data;
			if (configs.length === 3 || configs.length === 4) {
				// Group by risk tier - support 3 or 4 tiers
				const tiersMap: Partial<Record<RiskTier, RevenueConfigTierInput>> = {
					LOW: createEmptyTierConfig('LOW'),
					MEDIUM: createEmptyTierConfig('MEDIUM'),
					HIGH: createEmptyTierConfig('HIGH'),
				};

				// Only add OTHER if it exists in the response
				const hasOtherTier = configs.some((c) => c.risk_tier === 'OTHER');
				if (hasOtherTier) {
					tiersMap.OTHER = createEmptyTierConfig('OTHER');
				}

				configs.forEach((config) => {
					tiersMap[config.risk_tier as RiskTier] = {
						risk_tier: config.risk_tier as RiskTier,
						// Legacy rates
						discount_buy_rate: config.discount_buy_rate,
						authorization_buy_rate: config.authorization_buy_rate,
						// NEW: Split discount rates
						discount_buy_rate_vmd: config.discount_buy_rate_vmd,
						discount_buy_rate_amex: config.discount_buy_rate_amex,
						// NEW: Split auth fees
						auth_buy_rate_standard: config.auth_buy_rate_standard,
						auth_buy_rate_pin_debit: config.auth_buy_rate_pin_debit,
						auth_buy_rate_ebt: config.auth_buy_rate_ebt,
						// NEW: Batch fee
						batch_fee_rate: config.batch_fee_rate,
						// NEW: Dynamic monthly fees (with fallback to legacy)
						monthly_fee_costs:
							config.monthly_fee_costs && config.monthly_fee_costs.length > 0
								? config.monthly_fee_costs
								: [],
						// Legacy monthly fees
						monthly_fee_cost_1: config.monthly_fee_cost_1,
						monthly_fee_cost_1_label: config.monthly_fee_cost_1_label,
						monthly_fee_cost_2: config.monthly_fee_cost_2,
						monthly_fee_cost_2_label: config.monthly_fee_cost_2_label,
						monthly_fee_cost_3: config.monthly_fee_cost_3,
						monthly_fee_cost_3_label: config.monthly_fee_cost_3_label,
						agent_revenue_share_percent: config.agent_revenue_share_percent,
					};
				});

				setConfiguration({
					name: configs[0].name,
					description: configs[0].description,
					is_active: configs[0].is_active,
					tiers: tiersMap as any,
				});
			}
		}
	}, [editConfigId, existingConfigResponse]);

	const updateConfiguration = (updates: Partial<RevenueConfiguration>) => {
		setConfiguration((prev) => ({ ...prev, ...updates }));
	};

	const isCurrentStepValid = () => {
		switch (currentStep) {
			case 1:
				return configuration.name.trim() !== '';
			case 2:
				// Check that all required fields are filled for all tiers
				return Object.values(configuration.tiers).every(
					(tier) =>
						tier.discount_buy_rate >= 0 &&
						tier.authorization_buy_rate >= 0 &&
						tier.agent_revenue_share_percent >= 0 &&
						tier.agent_revenue_share_percent <= 100,
				);
			default:
				return false;
		}
	};

	const handleSave = async () => {
		try {
			// Convert tiers object to array
			const configurationsArray = Object.values(configuration.tiers);

			const request = {
				name: configuration.name,
				description: configuration.description,
				is_active: configuration.is_active,
				configurations: configurationsArray,
			};

			let result;
			if (editConfigId) {
				// Update existing configuration
				result = await updateRevenueConfig({
					id: editConfigId,
					config: request,
				});
			} else {
				// Create new configuration
				result = await createRevenueConfig(request);
			}

			if ('data' in result && result.data?.success) {
				showToast(
					'Success',
					'Revenue configuration saved successfully!',
					'success',
				);
				onComplete(configuration.name);
			} else if ('error' in result) {
				const errorMessage =
					(result.error as any)?.data?.message ||
					'Failed to save revenue configuration';
				showToast('Error', errorMessage, 'error');
			} else {
				showToast('Error', 'Failed to save revenue configuration', 'error');
			}
		} catch (error) {
			console.error('Error saving revenue configuration:', error);
			showToast('Error', 'Failed to save revenue configuration', 'error');
		}
	};

	const handleNext = () => {
		if (currentStep < STEPS.length && isCurrentStepValid()) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePrevious = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	const renderNavigationButtons = () => {
		const isFirstStep = currentStep === 1;
		const isLastStep = currentStep === STEPS.length;
		const isStepValid = isCurrentStepValid();
		const isNextDisabled = !editConfigId && !isStepValid;

		return (
			<div className="flex justify-between items-center">
				{/* Left Button */}
				<button
					onClick={isFirstStep ? onClose : handlePrevious}
					className="w-[200px] flex justify-center items-center px-8 py-3 bg-white border border-gray-300 hover:bg-gray-100 disabled:bg-gray-400 text-gray-500 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{!isFirstStep && <ChevronLeft className="w-5 h-5 mr-1" />}
					{isFirstStep ? 'Cancel' : 'Previous'}
				</button>

				{/* Center - Step Indicator */}
				<div className="flex items-center text-gray-500">
					<span className="text-md">
						Step {currentStep} of {STEPS.length}
					</span>
				</div>

				{/* Right Button */}
				<button
					onClick={isLastStep ? handleSave : handleNext}
					disabled={isCreating || isUpdating || isNextDisabled}
					className="w-[200px] flex justify-center items-center px-8 py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isLastStep ? (
						<>
							{isCreating || isUpdating ? (
								<>
									<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
									{editConfigId ? 'Updating...' : 'Creating...'}
								</>
							) : (
								<>
									<Check className="w-5 h-5 mr-1" />
									{editConfigId ? 'Update' : 'Create'}
								</>
							)}
						</>
					) : (
						<>
							Next
							<ChevronRight className="w-5 h-5 ml-1" />
						</>
					)}
				</button>
			</div>
		);
	};

	const renderCurrentStep = () => {
		switch (currentStep) {
			case 1:
				return (
					<BasicInfoStep
						configuration={configuration as any}
						onUpdate={updateConfiguration as any}
						onNext={handleNext}
						onCancel={onClose}
						hideNavigation={isFullPage}
						flagChecked={configuration.is_active}
						onFlagChange={(checked) =>
							updateConfiguration({ is_active: checked })
						}
						flagLabel="Set as Active Configuration"
					/>
				);
			case 2:
				return (
					<RiskTierStep
						configuration={configuration}
						onUpdate={updateConfiguration}
						hideNavigation={isFullPage}
					/>
				);
			default:
				return null;
		}
	};

	// Show loading state when fetching existing config
	if (editConfigId && isLoadingConfig) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
					<p className="text-gray-600">Loading configuration...</p>
				</div>
			</div>
		);
	}

	// Show error state if config failed to load
	if (editConfigId && !isLoadingConfig && !existingConfigResponse?.data) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<p className="text-red-600 mb-4">Failed to load configuration</p>
					<button
						onClick={onClose}
						className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
					>
						Close
					</button>
				</div>
			</div>
		);
	}

	const containerClasses = isFullPage
		? 'h-[calc(100vh-110px)] mt-10 bg-gray-50 flex flex-col'
		: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';

	const contentClasses = isFullPage
		? 'w-full max-w-6xl mx-auto flex-1 flex flex-col min-h-0'
		: 'bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden';

	return (
		<div className={containerClasses}>
			<div className={contentClasses}>
				<div className="flex flex-col h-full">
					{/* Header */}
					<div
						className={`flex-shrink-0 flex items-center justify-between ${
							isFullPage ? 'mb-8' : 'p-6 border-b border-gray-200'
						}`}
					>
						<div className="flex items-center space-x-4">
							{isFullPage && onBack && (
								<button
									onClick={onBack}
									className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
								>
									<ArrowLeft className="w-6 h-6 text-gray-600" />
								</button>
							)}
							<div>
								<h2 className="text-2xl font-bold text-gray-900">
									{editConfigId
										? 'Edit Revenue Configuration'
										: 'Create Revenue Configuration'}
								</h2>
								<p className="text-gray-600 mt-1">
									{STEPS[currentStep - 1].title}
								</p>
							</div>
						</div>
						{!isFullPage && (
							<button
								onClick={onClose}
								className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
							>
								<X className="w-6 h-6 text-gray-600" />
							</button>
						)}
					</div>

					{/* Steps Indicator */}
					<div
						className={`flex-shrink-0 flex items-center justify-center space-x-4 ${
							isFullPage ? 'mb-8' : 'px-6 py-4'
						}`}
					>
						{STEPS.map((step, index) => {
							const isActive = currentStep === step.id;
							const isCompleted = currentStep > step.id;
							const isAccessible = currentStep >= step.id;

							return (
								<React.Fragment key={step.id}>
									<button
										onClick={() => isAccessible && setCurrentStep(step.id)}
										disabled={!isAccessible}
										className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
											isActive
												? 'bg-purple-500 text-white'
												: isCompleted
													? 'bg-green-500 text-white'
													: 'bg-gray-200 text-gray-500'
										} ${isAccessible ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed opacity-50'}`}
									>
										<div
											className={`flex items-center justify-center w-6 h-6 rounded-full ${
												isActive
													? 'bg-white text-purple-500'
													: isCompleted
														? 'bg-white text-green-500'
														: 'bg-gray-300 text-gray-600'
											}`}
										>
											{isCompleted ? (
												<Check className="w-4 h-4" />
											) : (
												<span className="text-sm font-bold">{step.id}</span>
											)}
										</div>
										<span className="font-medium">{step.name}</span>
									</button>
									{index < STEPS.length - 1 && (
										<div className="w-8 h-0.5 bg-gray-300"></div>
									)}
								</React.Fragment>
							);
						})}
					</div>

					{/* Content */}
					<div
						className={`flex-1 overflow-y-auto ${
							isFullPage ? 'px-0' : 'px-6 py-6'
						}`}
					>
						{renderCurrentStep()}
					</div>

					{/* Footer Navigation */}
					{isFullPage && (
						<div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 mt-6">
							{renderNavigationButtons()}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default RevenueConfigWizard;
